import React, { useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import {
  normalizeNotifyChannelStrings,
  resolveNotifyChipItems,
} from "../../utils/alertNotifyChannels";

export { normalizeNotifyChannelStrings } from "../../utils/alertNotifyChannels";

/**
 * Dashboard rule rows `{ label, enabled }[]` → chip labels (deduped), same wording as History.
 * @param {Array<{ label?: string; enabled?: boolean }> | undefined} channels
 * @returns {string[]}
 */
export function notifyStringsFromDashboardRuleChannels(channels) {
  if (!Array.isArray(channels) || channels.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const c of channels) {
    const lab = String(c?.label || "").trim();
    if (!lab) continue;
    const normalized = normalizeNotifyChannelStrings([lab])[0] || lab;
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    out.push(normalized);
  }
  return out;
}

const SUCCESS = {
  bg: "#E8F5E9",
  color: "#1B5E20",
  icon: "#2E7D32",
};

const FAIL = {
  bg: "#FFEBEE",
  color: "#C62828",
  icon: "#C62828",
};

/**
 * Green send icon (delivered) or red send with diagonal strike (failed / partial).
 */
function OutcomeSendIcon({ ok }) {
  if (ok) {
    return <SendIcon sx={{ fontSize: 14, color: SUCCESS.icon, flexShrink: 0 }} />;
  }
  return (
    <Box
      sx={{
        position: "relative",
        width: 18,
        height: 18,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
      aria-hidden
    >
      <SendIcon
        sx={{
          fontSize: 14,
          color: FAIL.icon,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: "120%",
          height: 2,
          borderRadius: 1,
          bgcolor: FAIL.icon,
          transform: "rotate(-38deg)",
          top: "50%",
          left: "50%",
          marginTop: "-1px",
          marginLeft: "-60%",
        }}
      />
    </Box>
  );
}

/**
 * "Notified by" — one pill per channel: label + status-colored send icon (Alert Services dashboard / history).
 * @param {{
 *   channels?: string[];
 *   notifyChannelItems?: { label: string; outcome: string }[];
 *   notificationStatus?: unknown;
 *   row?: Record<string, unknown>;
 *   stackSx?: object;
 * }} props
 */
export default function AlertNotifiedByChips({
  channels = [],
  notifyChannelItems,
  notificationStatus,
  row,
  stackSx,
}) {
  const items = useMemo(
    () =>
      resolveNotifyChipItems(
        row ?? {
          notifyChannelItems,
          notifyChannels: channels,
          notificationStatus,
        },
      ),
    [row, notifyChannelItems, channels, notificationStatus],
  );

  if (!items || items.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        —
      </Typography>
    );
  }

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={0.5}
      sx={{ maxWidth: 280, alignItems: "center", ...stackSx }}
    >
      {items.map((item, idx) => {
        const ok =
          item.outcome === "success" ||
          (item.outcome !== "failed" && item.outcome !== "partial");
        const palette = ok ? SUCCESS : FAIL;
        return (
          <Box
            key={`${item.label}-${idx}`}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 0.875,
              py: 0.25,
              borderRadius: "10px",
              bgcolor: palette.bg,
              color: palette.color,
              minHeight: 22,
              maxWidth: "100%",
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: "0.7rem",
                fontWeight: 600,
                lineHeight: 1.2,
                color: "inherit",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.label}
            </Typography>
            <OutcomeSendIcon ok={ok} />
          </Box>
        );
      })}
    </Stack>
  );
}
